/**
 * 智能体中心相关 API
 */
import { get, post, del, put } from '@/utils/request'
import { getUserApiKey } from '@/utils/digitalEmployeeProject'

/** 数字员工接口：Header 携带 user-key；走站点根路径 /ai_employee（非 /agent-dev） */
function withUserKey(options = {}) {
  const userKey = getUserApiKey()
  const next = {
    ...options,
    apiScope: 'siteRoot',
  }
  if (!userKey) return next
  return {
    ...next,
    headers: {
      ...(options.headers || {}),
      'user-key': userKey,
    },
  }
}

/** 获取智能体列表 */
export async function getAgentList(params = {}) {
  return get('/agentHub/list', params)
}

/** 获取智能体导航分类导航 */
export async function getAgentCategory(params = {}) {
  return get('/agentHub/categories', params)
}

/** 获取智能体标签 */
export async function getAgentTag(params = {}) {
  return get('/agentHub/tags', params)
}



/** 进入智能体详情页记录一次使用（Header 携带 token，body: { agentId }） */
export async function useAgentRecords(data) {
  return post('/agentHub/use', data)
}

/** 收藏/取消收藏智能体（Header 携带 token，body: { agentId }） */
export async function collectAgent(data) {
  return post('/agentHub/favorite/toggle', data)
}

/** 智能体收藏列表（Header 携带 token，query: pageNum、pageSize） */
export async function favoriteAgentsList(params = {}) {
  return get('/agentHub/favorite/list', params)
}

/** 智能体详情（query: agentId） */
export async function getAgentDetail(params = {}) {
  return get('/agentHub/detail', params)
}

/** 工作流商店接口（与 `workflow.js` 一致，便于从 agents 模块统一按需引用） */
export { getWorkflowList, getWorkTypeList, getWorkflowDetail } from './workflow'

/**
 * 数字员工项目列表，此接口需要header携带user-key
 */
export async function getDigitalEmployeeProjectList(params = {}) {
  return get('/ai_employee/projects', params, withUserKey())
}
/**
 * 添加数字员工项目，此接口需要header携带user-key
 */
export async function addDigitalEmployeeProject(data) {
  return post('/ai_employee/projects', data, withUserKey())
}
/**
 * 删除数字员工项目，此接口需要header携带user-key，DEL方法
 * @param {string|number} projectId
 */
export async function deleteDigitalEmployeeProject(projectId) {
  return del(`/ai_employee/projects/${projectId}`, withUserKey())
}
/**
 * 数字员工项目修改，此接口需要header携带user-key，PUT方法
 * @param {string|number} projectId
 */
export async function updateDigitalEmployeeProject(projectId, data = {}) {
  return put(`/ai_employee/projects/${projectId}`, data, withUserKey())
}
/**
 * 数字员工项目详情，此接口需要header携带user-key，GET方法
 */
export async function getDigitalEmployeeProjectDetail(projectId, params = {}) {
  return get(`/ai_employee/agent-projects-step/${projectId}`, params, withUserKey())
}
/**
 * 数字员工项目详情添加，此接口需要header携带user-key，POST方法
 */
export async function addDigitalEmployeeProjectDetail(data, step, projectId) {
  return post(`/ai_employee/agent-projects-step/${projectId}/steps/${step}/save`, data, withUserKey())
}

/**
 * 数字员工项目详情修改，此接口需要header携带user-key，POST方法
 * @param {object} data - 须含 id（当前节点 id）；单步修改 pid=0、sort=0；分步修改 pid=父节点 id、sort=子节点序号
 */
export async function updateDigitalEmployeeProjectDetail(data, step, projectId) {
  return post(`/ai_employee/agent-projects-step/${projectId}/steps/${step}/edit`, data, withUserKey())
}

/**
 * 多模态文字生成模型接口
 */
export async function getMultimodalTextGenerationModel(data) {
  return post('/ai_employee/ai-texts/generate', data, withUserKey())
}
/**
 * 图片生成模型
 */
export async function getImageGenerationModel(data) {
  return post('/ai_employee/ai-images/generate', data, withUserKey())
}
/**
 * 图片生成任务查询，用户轮询查询生成任务状态
 */
export async function getImageGenerationTask(taskId) {
  return get(`/ai_employee/ai-images/tasks/${taskId}`, {}, withUserKey())
}
/**
 * 视频生成模型
 */
export async function getVideoGenerationModel(data) {
  return post('/ai_employee/ai-videos/generate', data, withUserKey())
}
/**
 * 视频生成任务查询，用户轮询查询生成任务状态
 */
export async function getVideoGenerationTask(taskId) {
  return get(`/ai_employee/ai-videos/tasks/${taskId}`, {}, withUserKey())
}
/**
 * 获取提示词模板（query: templateKey，如 ds_key0）
 * @param {string} templateKey - 模板键
 */
export async function getPrompt(templateKey) {
  return get('/ai_employee/prompts/templatesDetail', { templateKey }, withUserKey())
}
/**
 * 智能体任务详情步骤删除（steps 后为步骤序号：0、1、2…，非节点数据库 id）
 * @param {string|number} taskId 项目 ID
 * @param {string|number} stepOrder 步骤序号
 */
export async function deleteAgentTaskDetail(taskId, stepOrder) {
  return del(`/ai_employee/agent-projects-step/${taskId}/steps/${stepOrder}`, withUserKey())
}